import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ProjectElement } from '../project-element.interface';
import { ProjectElementSidebarState, TemplateFormControls } from './element.sidebar.interface';
import { take } from 'rxjs/operators';
import { FormGroup } from '@angular/forms';

/**
 * Element sidebar.
 *
 * @note Currently custom made for elements, but setup is quite generic. When there needs to
 *  be sidebar support for other data types, it shouldn't be hard to convert it to load custom
 *  datatypes.
 */
@Injectable({
  providedIn: 'root'
})
export class ElementSidebarService {

  /** Default state. */
  private readonly DEFAULT_STATE = { open: false, tab: null, item: null, mode: 'update' };

  /** Sidebar state Subject. */
  private _active = new BehaviorSubject<ProjectElementSidebarState>(this.DEFAULT_STATE);

  /** Sidebar state Observable */
  public active$ = this._active.asObservable();

  constructor() { }

  /**
   * Dismiss the sidebar.
   */
  public dismiss(): void {
    document.body.classList.remove('sidebar-active');
    this._active.next(this.DEFAULT_STATE);
  }

  /**
   * Check if the sidebar is currently open.
   *
   * @returns Whether the sidebar is currently open.
   */
  public isOpen(): boolean {
    let state: ProjectElementSidebarState;
    this.active$.pipe(take(1)).subscribe(s => {
      state = s;
    });
    return state.open;
  }

  /**
   * Open the sidebar in 'create' mode.
   *
   * @todo Move to `ElementService`.
   *
   * @param template Template to be used for new element. Can be empty.
   */
  public new(template: Partial<ProjectElement> = {}, formControls: TemplateFormControls<ProjectElement> = {}): void {
    document.body.classList.add('sidebar-active');
    this._active.next({
      open: true,
      tab: 0,
      item: template,
      formControls: formControls,
      mode: 'create'
    });
  }

  /**
   * Open sidebar.
   *
   * @param elementId The element's id.
   * @param [tab=details] The default open tab.
   */
  public open(element: ProjectElement, tab: 'details' | 'logs' | 'history' = 'details'): void {
    document.body.classList.add('sidebar-active');
    const index = ['details', 'logs', 'history'].indexOf(tab);
    const selected = index > -1 ? index : 0;
    this._active.next({ open: true, tab: selected, item: element, mode: 'update' });
  }

  /**
   * Update current active tab state.
   *
   * @param index The index of the current tab.
   */
  public onTabChange(index: ProjectElementSidebarState['tab']): void {
    let current: ProjectElementSidebarState;
    this.active$.pipe(take(1)).subscribe(state => {
      current = state;
    });
    this._active.next({
      ...current,
      tab: index
    });
  }
}
